import { useState, useEffect, useCallback } from "react";
import type { CmmdProjectContextLink } from "@cmmd-forge/contracts";
import { invalidateProjectLinkList } from "../cmmd/projectLinkListResource";
import { cmmdApiClient } from "../environments/cmmdApi";

export interface CmmdProjectLink {
  readonly forgeEnvironmentId: string;
  readonly forgeProjectId: string;
  readonly forgeWorkspaceRoot: string;
  readonly cmmdOrganizationId: number;
  readonly cmmdProjectId: number;
  readonly cmmdCampaignId: number | null;
  readonly cmmdWorkspaceId: number;
  readonly cmmdWorkspaceName: string;
  readonly workspaceName: string;
  readonly cmmdCampaignName: string | null;
  readonly campaignName: string | null;
  readonly cmmdProjectName: string;
  readonly projectName: string;
  readonly githubRepoFullName: string | null;
  readonly source: "linked_existing" | "created_from_cmmd" | "created_from_forge";
  readonly linkedByUserId: string | null;
  readonly linkedAt: string;
  readonly lastVerifiedAt: string | null;
}

export interface UseCmmdProjectLinkInput {
  readonly environmentId: string;
  readonly projectId: string;
  readonly cwd: string;
  readonly githubRepoFullName?: string | null;
}

export interface UseCmmdProjectLinkResult {
  link: CmmdProjectLink | null;
  contextLinks: readonly CmmdProjectContextLink[];
  loading: boolean;
  contextLinksLoading: boolean;
  setLink: (
    link: Omit<
      CmmdProjectLink,
      "forgeEnvironmentId" | "forgeProjectId" | "forgeWorkspaceRoot" | "linkedByUserId"
    >,
  ) => Promise<void>;
  selectContext: (context: CmmdProjectContextLink) => Promise<void>;
  deleteLink: () => Promise<void>;
}

export function useCmmdProjectLink(
  input: UseCmmdProjectLinkInput | null,
): UseCmmdProjectLinkResult {
  const [link, setLinkState] = useState<CmmdProjectLink | null>(null);
  const [contextLinks, setContextLinks] = useState<readonly CmmdProjectContextLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [contextLinksLoading, setContextLinksLoading] = useState(false);
  const environmentId = input?.environmentId ?? "";
  const projectId = input?.projectId ?? "";
  const cwd = input?.cwd ?? "";
  const githubRepoFullName = input?.githubRepoFullName ?? null;

  useEffect(() => {
    if (!environmentId || !projectId || !cwd) {
      setLoading(false);
      setLinkState(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    cmmdApiClient
      .getProjectLink({ environmentId, projectId, cwd })
      .then((data) => {
        if (cancelled) return;
        if (
          data.linked &&
          data.forgeEnvironmentId !== undefined &&
          data.forgeProjectId !== undefined &&
          data.forgeWorkspaceRoot !== undefined &&
          data.cmmdProjectId !== undefined &&
          data.cmmdWorkspaceId !== undefined
        ) {
          const cmmdWorkspaceName = data.cmmdWorkspaceName ?? data.workspaceName ?? "";
          const cmmdCampaignName = data.cmmdCampaignName ?? data.campaignName ?? null;
          const cmmdProjectName = data.cmmdProjectName ?? data.projectName ?? "";
          setLinkState({
            forgeEnvironmentId: data.forgeEnvironmentId,
            forgeProjectId: data.forgeProjectId,
            forgeWorkspaceRoot: data.forgeWorkspaceRoot,
            cmmdOrganizationId: data.cmmdOrganizationId ?? data.cmmdWorkspaceId,
            cmmdProjectId: data.cmmdProjectId,
            cmmdCampaignId: data.cmmdCampaignId ?? null,
            cmmdWorkspaceId: data.cmmdWorkspaceId,
            cmmdWorkspaceName,
            workspaceName: cmmdWorkspaceName,
            cmmdCampaignName,
            campaignName: cmmdCampaignName,
            cmmdProjectName,
            projectName: cmmdProjectName,
            githubRepoFullName: data.githubRepoFullName ?? null,
            source: data.source ?? "linked_existing",
            linkedByUserId: data.linkedByUserId ?? null,
            linkedAt: data.linkedAt ?? new Date().toISOString(),
            lastVerifiedAt: data.lastVerifiedAt ?? null,
          });
        } else {
          setLinkState(null);
        }
      })
      .catch(() => {
        if (!cancelled) setLinkState(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cwd, environmentId, projectId]);

  useEffect(() => {
    if ((!environmentId || !projectId) && !githubRepoFullName) {
      setContextLinks([]);
      setContextLinksLoading(false);
      return;
    }
    let cancelled = false;
    setContextLinksLoading(true);
    cmmdApiClient
      .listProjectContextLinks({
        ...(environmentId ? { environmentId } : {}),
        ...(projectId ? { projectId } : {}),
        ...(githubRepoFullName ? { githubRepoFullName } : {}),
      })
      .then((links) => {
        if (!cancelled) setContextLinks(links);
      })
      .catch(() => {
        if (!cancelled) setContextLinks([]);
      })
      .finally(() => {
        if (!cancelled) setContextLinksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [environmentId, githubRepoFullName, projectId]);

  const setLink = useCallback(
    async (
      newLink: Omit<
        CmmdProjectLink,
        "forgeEnvironmentId" | "forgeProjectId" | "forgeWorkspaceRoot" | "linkedByUserId"
      >,
    ) => {
      const durableLink: CmmdProjectLink = {
        ...newLink,
        forgeEnvironmentId: environmentId,
        forgeProjectId: projectId,
        forgeWorkspaceRoot: cwd,
        linkedByUserId: null,
      };
      await cmmdApiClient.setProjectLink({ environmentId, projectId, cwd, ...durableLink });
      setLinkState(durableLink);
      // Every surface that draws this project resolves its identity through the
      // shared mapping. Leaving that stale shows the previous project's icon,
      // which is worse than showing none.
      invalidateProjectLinkList();
    },
    [cwd, environmentId, projectId],
  );

  const deleteLink = useCallback(async () => {
    await cmmdApiClient.deleteProjectLink({
      environmentId,
      projectId,
      cwd,
      cmmdProjectId: link?.cmmdProjectId ?? null,
    });
    setLinkState(null);
    invalidateProjectLinkList();
  }, [cwd, environmentId, link?.cmmdProjectId, projectId]);

  const selectContext = useCallback(
    async (context: CmmdProjectContextLink) => {
      const workspaceName =
        link?.cmmdOrganizationId === context.organizationId
          ? link.cmmdWorkspaceName
          : (link?.cmmdWorkspaceName ?? "CMMD");
      await setLink({
        cmmdOrganizationId: context.organizationId,
        cmmdProjectId: context.projectId,
        cmmdCampaignId: context.projectCampaignId,
        cmmdWorkspaceId: context.organizationId,
        cmmdWorkspaceName: workspaceName,
        workspaceName,
        cmmdCampaignName: null,
        campaignName: null,
        cmmdProjectName: context.projectTitle,
        projectName: context.projectTitle,
        githubRepoFullName: context.githubRepoFullName,
        source: context.source,
        linkedAt: context.linkedAt,
        lastVerifiedAt: context.lastVerifiedAt,
      });
    },
    [link?.cmmdOrganizationId, link?.cmmdWorkspaceName, setLink],
  );

  return { link, contextLinks, loading, contextLinksLoading, setLink, selectContext, deleteLink };
}
